//@flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm, Field, type FormProps } from 'redux-form';
import type { Dispatch, BrandingSettings } from 'state/types';
import {
  getAdminProperties,
  setBrandingSettings,
} from 'state/commands/adminPropertiesCommands';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import { getBrandingSettings } from 'state/selectors';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import { CompactPicker } from 'react-color';
import { BRANDING_SETTINGS_FORM } from 'util/constants';

type Props = {
  getAdminProperties: typeof getAdminProperties,
  setBrandingSettings: typeof setBrandingSettings,
  brandingSettings: BrandingSettings,
} & FormProps;

type State = {
  backgroundColorChanged: boolean,
  newBackgroundColor: string,
  customImageChanged: boolean,
  imageUpload: any,
};

class LoginCustomizationPage extends Component<Props, State> {
  submit: Function;
  onImageSelected: Function;
  onColorChangeComplete: Function;
  isSubmitDisabled: Function;
  onRemoveImage: Function;

  state = {
    backgroundColorChanged: false,
    newBackgroundColor: '',
    customImageChanged: false,
    imageUpload: undefined,
  };

  constructor() {
    super();
    this.submit = this.submit.bind(this);
    this.onImageSelected = this.onImageSelected.bind(this);
    this.onColorChangeComplete = this.onColorChangeComplete.bind(this);
    this.onRemoveImage = this.onRemoveImage.bind(this);
    this.state.backgroundColorChanged = false;
  }

  componentDidMount() {
    this.props.getAdminProperties([ADMIN_PROPERTIES.brandingSettings]);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.brandingSettings !== nextProps.brandingSettings) {
      this.initializeFields(nextProps.brandingSettings);
    }
  }

  initializeFields(brandingSettings: BrandingSettings) {
    if (brandingSettings.useCustomBranding)
      this.props.initialize(brandingSettings);
  }

  onImageSelected(e: any) {
    const { brandingSettings } = this.props;

    this.setState({
      imageUpload: e.target.files[0],
    });

    brandingSettings.customImageFilename = e.target.files[0].name;
    this.setState({ customImageChanged: true });

    this.forceUpdate();
  }

  onRemoveImage() {
    this.props.brandingSettings.customImageFilename = '';
    this.props.brandingSettings.useCustomImage = false;
    this.setState({ customImageChanged: true, imageUpload: undefined });
  }

  onColorChangeComplete(color) {
    this.setState({ newBackgroundColor: color.hex });
    this.setState({ backgroundColorChanged: true });
  }

  isSubmitDisabled(formIsPristine: boolean) {
    if (this.state.backgroundColorChanged) return false;
    if (this.state.customImageChanged) return false;
    if (formIsPristine) return true;
  }

  submit(values, dispatch, props) {
    const { setBrandingSettings } = this.props;
    const { imageUpload, newBackgroundColor } = this.state;
    const settings: BrandingSettings = {
      ...values,
      customBackgroundColor: newBackgroundColor.replace('#', ''), // get rid of the leading #
    };

    let imageData = {};
    imageData.file = imageUpload;

    setBrandingSettings(settings, imageData);
    this.setState({ customImageChanged: false });
  }

  render() {
    const {
      handleSubmit,
      pristine,
      reset,
      brandingSettings,
      intl: { formatMessage },
    } = this.props;

    return (
      <div
        className="page page-contents-textured"
        data-test-name="login-customization-page"
      >
        <header className="page-header">
          <FormattedMessage
            {...localizedStrings.GLOBAL_SETTINGS_CUSTOMIZED_LOGIN_SCREEN}
          />
        </header>

        <div className="portfolio-form-container">
          <div className="box">
            <form
              onSubmit={handleSubmit(this.submit)}
              style={{ maxWidth: '700px' }}
            >
              {/* Custom Branding Enabled */}
              <div className="field is-horizontal">
                <Field
                  className="login-customization-field-xsmall-width is-checkradio is-medium"
                  component={CheckRadio}
                  id="useCustomBranding"
                  name="useCustomBranding"
                  type="checkbox"
                />
                <label
                  className="label is-medium login-customization-enabled-label"
                  htmlFor="useCustomBranding"
                >
                  {formatMessage(
                    localizedStrings.GLOBAL_SETTINGS_CUSTOMIZED_LOGIN_SCREEN
                  )}
                </label>
              </div>
              <div className="vertical-spacer-large" />

              {/* Custom Image */}
              <div className="field is-horizontal has-addons">
                <div className="field-label is-normal">
                  <label className="label">
                    {formatMessage(
                      localizedStrings.GLOBAL_SETTINGS_CUSTOM_IMAGE
                    )}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <div className="file is-right is-fullwidth">
                        <label className="file-label">
                          <input
                            className="file-input"
                            type="file"
                            name="imageUpload"
                            accept=".png,.jpg,.jpeg"
                            onChange={this.onImageSelected}
                          />
                          <span className="file-cta">
                            <span className="file-label">
                              {formatMessage(
                                localizedStrings.GLOBAL_SETTINGS_CHOOSE_FILE
                              )}
                            </span>
                          </span>
                          <span className="file-name">
                            {brandingSettings.customImageFilename
                              ? brandingSettings.customImageFilename
                              : formatMessage(
                                  localizedStrings.NO_FILE_SELECTED
                                )}
                          </span>
                        </label>
                        {brandingSettings.customImageFilename && (
                          <div
                            className="delete"
                            onClick={this.onRemoveImage}
                            style={{ margin: '8px' }}
                          />
                        )}
                      </div>

                      <div className="help is-indented">
                        {formatMessage(
                          localizedStrings.GLOBAL_SETTINGS_CUSTOM_IMAGE_INFO_TEXT
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Title */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">
                    {formatMessage(
                      localizedStrings.GLOBAL_SETTINGS_CUSTOM_TITLE
                    )}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input"
                        component="input"
                        id="customTitle"
                        name="customTitle"
                        type="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Custom Link */}
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">
                    {formatMessage(localizedStrings.GLOBAL_SETTINGS_IMAGE_URL)}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <div className="control is-expanded">
                      <Field
                        className="input"
                        component="input"
                        id="customURL"
                        name="customURL"
                        type="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">
                    {formatMessage(
                      localizedStrings.GLOBAL_SETTINGS_BACKGROUND_COLOR
                    )}
                  </label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <CompactPicker
                      color={
                        this.state.newBackgroundColor
                          ? this.state.newBackgroundColor
                          : brandingSettings.customBackgroundColor
                      }
                      onChangeComplete={this.onColorChangeComplete}
                    />
                  </div>
                </div>
              </div>
              <div className="vertical-spacer-large" />

              <div className="field is-grouped is-pulled-right">
                <div className="control">
                  <button
                    className="button is-dark is-outlined"
                    disabled={pristine}
                    onClick={reset}
                    type="button"
                  >
                    <FormattedMessage {...localizedStrings.REVERT} />
                  </button>
                </div>
                <div className="control">
                  <button
                    className={'button is-primary'}
                    type="submit"
                    disabled={this.isSubmitDisabled(pristine)}
                  >
                    <FormattedMessage {...localizedStrings.APPLY} />
                  </button>
                </div>
              </div>
              <div className="vertical-spacer-xlarge" />
            </form>
          </div>
        </div>
      </div>
    );
  }
}

const loginCustomizationForm = reduxForm({
  destroyOnUnmount: false,
  form: BRANDING_SETTINGS_FORM,
})(injectIntl(LoginCustomizationPage));

export default connect(
  state => ({
    brandingSettings: getBrandingSettings(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      getAdminProperties,
      setBrandingSettings,
    })
)(loginCustomizationForm);



// WEBPACK FOOTER //
// ./src/view/Pages/GlobalSettingsPages/LoginCustomizationPage.js